/**
 * Field tree walking and type checking utilities
 */

import type { SchemaField } from '@core-engine/schema/types.js'

// ============================================================================
// Field Tree Walking
// ============================================================================

export interface WalkFieldsOptions {
  includeLists?: boolean
  includeGroups?: boolean
  includeTabs?: boolean
}

const DEFAULT_WALK_OPTIONS: WalkFieldsOptions = {
  includeLists: true,
  includeGroups: true,
  includeTabs: true
}

/**
 * Walk through all fields in a schema, including nested ones
 */
export function walkFields(
  fields: SchemaField[],
  callback: (field: SchemaField, depth: number, parent?: SchemaField) => void,
  options: WalkFieldsOptions = DEFAULT_WALK_OPTIONS
): void {
  function walk(fieldsToWalk: SchemaField[], depth: number, parent?: SchemaField): void {
    for (const field of fieldsToWalk) {
      callback(field, depth, parent)

      if (
        options.includeGroups &&
        (field.type === 'group' || field.type === 'section') &&
        field.fields
      ) {
        walk(field.fields, depth + 1, field)
      }
      if (options.includeLists && field.type === 'list' && field.fields) {
        walk(field.fields, depth + 1, field)
      }
      if (options.includeTabs && field.type === 'tabs' && field.tabs) {
        for (const tab of field.tabs) {
          if (tab.fields) {
            walk(tab.fields, depth + 1, field)
          }
        }
      }
    }
  }

  walk(fields, 0)
}

// ============================================================================
// Field Type Checking
// ============================================================================

/**
 * Check if any field in the tree matches a specific type
 */
export function hasFieldType(
  fields: SchemaField[],
  type: string,
  options?: WalkFieldsOptions
): boolean {
  let found = false
  walkFields(
    fields,
    (field) => {
      if (field.type === type) found = true
    },
    options
  )
  return found
}

/**
 * Check if any field in the tree matches any of the given types
 */
export function hasAnyFieldType(
  fields: SchemaField[],
  types: string[],
  options?: WalkFieldsOptions
): boolean {
  let found = false
  walkFields(
    fields,
    (field) => {
      if (types.includes(field.type)) found = true
    },
    options
  )
  return found
}

/**
 * Check if any field uses icons (either icon type or hasIcon property)
 */
export function hasIconUsage(fields: SchemaField[], options?: WalkFieldsOptions): boolean {
  let found = false
  walkFields(
    fields,
    (field) => {
      if (field.type === 'icon' || field.hasIcon) found = true
    },
    options
  )
  return found
}

export function hasRelationshipField(fields: SchemaField[], options?: WalkFieldsOptions): boolean {
  return hasFieldType(fields, 'relationship', options)
}

export function hasMarkdownField(fields: SchemaField[], options?: WalkFieldsOptions): boolean {
  return hasFieldType(fields, 'markdown', options)
}

export function hasTextareaField(fields: SchemaField[], options?: WalkFieldsOptions): boolean {
  return hasFieldType(fields, 'text', options)
}

/**
 * Collect all fields of a specific type
 */
export function collectFieldsByType(
  fields: SchemaField[],
  type: string,
  options?: WalkFieldsOptions
): SchemaField[] {
  const result: SchemaField[] = []
  walkFields(
    fields,
    (field) => {
      if (field.type === type) result.push(field)
    },
    options
  )
  return result
}
